import {
	IChartOptions,
	IChartResult,
	IChartSerie,
	SimilarToArray
} from './chartSteps';

import barChart from './barChart';
import { ResultSet, TCubeMeasure, TimeDimension, TimeDimensionGranularity } from '@cubejs-client/core';
import areaChart from './areaChart';
import pieDonutChart from './pieDonutChart';
import lineChart from './lineChart';
import { cloneDeep, isArray, isNil } from 'lodash';
import { granularityDateFormater } from './chartSteps/dateFormatter';
import radarChart from './radarChart';

export function getChartProps(resultSet: ResultSet | SimilarToArray<ResultSet>, options: IChartOptions, isGroupedByWeek: boolean): IChartResult {
	const measures = Array.isArray(options.config)
		? options.config.reduce<TCubeMeasure[]>((acc, c) => acc.concat(...c.measures), [])
		: options.config.measures;

	if (!Array.isArray(measures) || measures.length === 0) {
		return {
			options: {},
			series: []
		};
	}

	if (isSingleResultSet(resultSet))
		// understand the right chart type
		return getChartResult(resultSet, options, isGroupedByWeek);

	if (!Array.isArray(options.config))
		throw 'Error';

	const resultSets: ResultSet[] = Array.from({
		...resultSet,
		length: Object.keys(resultSet).length
	});
	const preloadedAxisNames: string[] = [];

	const timeDimension = retrieveTimeDimension(resultSet[0]);
	let granularity: TimeDimensionGranularity = 'day';

	if (areTimeDimensionsDefined(resultSet[0]))
		granularity = (isGroupedByWeek ? 'week' : timeDimension!.granularity!);

	if (areAxisDefined(options)) {
		resultSets.forEach((rs, index) => {
			const rawAxisList: string[][] = options.config[index].axisDimensions.map(xaxis => rs.rawData().map(data => data[xaxis.name]));
			const axisResult: string[] = getAxisResult(rawAxisList, granularity, options);

			if (!isNil(resultSets))
				axisResult.forEach(a => {
					if (!preloadedAxisNames.includes(a) && !isNil(a))
						preloadedAxisNames.push(a);
				});
		});
	}

	const chartResults: IChartResult[] = resultSets.map((rs, i) => getChartResult(rs,
		{
			...options,
			config: options.config[i]
		},
		isGroupedByWeek,
		preloadedAxisNames.length > 0 ? preloadedAxisNames : null
	));

	const mergedSeries = chartResults.reduce<IChartSerie[]>((acc, c) =>
		isNumberArray(c.series)
			? acc
			: acc.concat(...c.series), []);

	const mergedColors = chartResults.reduce<Record<string, unknown>[]>((acc, c, i) => {
		const colors = [...(c.options.colors ?? [])];
		return acc.concat(...colors);
	},
		[]);

	const queryIndexes = chartResults.reduce<number[]>((acc, c, i) => {
		let indexes = [...(c.options.colors ?? [])];
		indexes = indexes.map(x => [i]);
		return acc.concat(...indexes);
	},
		[]);

	const mergedAxis = chartResults.reduce<string[]>((acc, c) => {
		const axis = (c.options.xaxis && c.options.xaxis.categories) || [];
		axis.forEach(element => {
			if (!acc.includes(element))
				acc.push(element);
		});
		return acc;
	}, []);

	if (!isNil(chartResults[0].options.xaxis))
		chartResults[0].options.xaxis.categories = mergedAxis;

	chartResults[0].series = mergedSeries;
	chartResults[0].options.colors = mergedColors;
	chartResults[0].queryIndexes = queryIndexes;

	return chartResults[0];
}

function isNumberArray<T>(array: T[] | number[]): array is number[] {
	return typeof array[0] === 'number';
}

function getChartResult(resultSet: ResultSet, options: IChartOptions, isGroupedByWeek: boolean, preloadedAxisNames: string[] | null = null): IChartResult {
	switch (options.chartType) {
		case 'area':
			return areaChart(resultSet, options, isGroupedByWeek, preloadedAxisNames);
		case 'bar':
			return barChart(resultSet, options, isGroupedByWeek, preloadedAxisNames);
		case 'line':
			return lineChart(resultSet, options, isGroupedByWeek, preloadedAxisNames);
		case 'pie':
			return pieDonutChart(resultSet, options, isGroupedByWeek);
		case 'radar':
			return radarChart(resultSet, options, isGroupedByWeek);
		default:
			return fromChartHelper();
	}
}

function isChartSerie(serie: IChartSerie | number): serie is IChartSerie {
	return typeof serie !== 'number' && Array.isArray(serie.data);
}

function isSingleResultSet(resultSet: ResultSet | SimilarToArray<ResultSet>): resultSet is ResultSet {
	return Object.keys(resultSet).length <= 1;
}

function fromChartHelper(): IChartResult {
	return {
		options: {},
		series: []
	};
}

function getAxisResult(rawAxisList: string[][], granularity: TimeDimensionGranularity, options: IChartOptions): string[] {
	const rawAxisCopy = cloneDeep(rawAxisList);
	rawAxisCopy.forEach((rawAxis, rawAxisindex) => {
		rawAxis.forEach((axis, axisindex) => {
			if (!isNil(axis)) {
				if (/\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d(?:\.\d+)?Z?/gm.test(axis))
					rawAxisCopy[rawAxisindex][axisindex] = granularityDateFormater(new Date(axis), granularity, options.languageCode);
			}
		});
	});

	const axisResult: string[] = cloneDeep(rawAxisCopy[0]);
	rawAxisCopy.forEach((a, indexA) => {
		a.forEach((b, indexB) => {
			if (!isNil(b) && !isNil(axisResult[indexB]) && indexA > 0) {
				axisResult[indexB] += ('|' + b);
			}
		});
	});
	return axisResult;
}

function areAxisDefined(options: IChartOptions): boolean {
	let result = false;
	if (isArray(options.config)) {
		options.config.forEach(c => {
			if (c.axisDimensions.length > 0)
				result = true;
		});
	}
	return result;
}

function areTimeDimensionsDefined(resultSet: ResultSet): boolean {
	const query = resultSet.query();

	if (isArray(query.timeDimensions) && query.timeDimensions.length > 0)
		return true;

	return false;
}

function retrieveTimeDimension(resultSet: ResultSet): TimeDimension | null {
	const rs = !isNil(resultSet[0]) ? resultSet[0] : resultSet;
	if (isNil(rs))
		return null;

	const query = rs.query();

	if (query.timeDimensions && query.timeDimensions.length)
		return query.timeDimensions![0];
	return null;
}

export * from './chartSteps';
